import React from 'react';
import { FlyerCampaign } from '../../../../../models/interfaces/FlyerCampaign.interface';
import CustomDialogComponent from '../../../../../commons/components/custom-dialog/custom-dialog.component';
import {
  Box,
  Button,
  Chip,
  Grid,
  InputAdornment,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  TextField,
} from '@mui/material';
import logger from '../../../../../commons/logger';
import flyerCampaignService from '../../../../../services/flyer-campaign.service';
import { BusinessObject } from '../../../../../models/interfaces/BusinessObject.interface';
import { User } from '../../../../../models/interfaces/User.interface';
import Divider from '@mui/material/Divider';
import businessObjectService from '../../../../../services/business-object.service';
import { StringUtils } from '../../../../../commons/utils/string.util';
import { Autocomplete } from '@mui/lab';
import { makeStyles } from '@mui/styles';
import userService from '../../../../../services/user.service';
import {
  DEFAULT_NUMBER_OF_CUSTOMER_FOR_FLYER_CAMPAIGN,
  FLYER_CAMPAIGN_PRICE_PER_SMS,
} from '../../../../../models/consts/Other.const';
import { ArrayUtils } from '../../../../../commons/utils/array.utils';
import InvoiceTableComponent from './invoice-table.component';
import { InvoiceDetail } from '../../../../../models/interfaces/InvoiceDetail.interface';
import adminService from '../../../../../services/admin.service';

const useStyles = makeStyles({
  optionDescription: {
    marginLeft: '5px',
    fontSize: 'small',
    color: 'grey',
  },
});

interface StepProps {
  flyerCampaign: FlyerCampaign;
  onDataChange: Function;
  businessObjects?: BusinessObject[];
  users?: User[];
}

function InfoStep(props: StepProps) {
  const [flyerCampaign, setFlyerCampaign] = React.useState<FlyerCampaign>(null);
  const [user, setUser] = React.useState<User>(null);
  const [excludedBos, setExcludedBos] = React.useState<BusinessObject[]>([]);

  React.useEffect(() => {
    setFlyerCampaign(props?.flyerCampaign);
    setUser(props?.users?.find(u => u.id === props?.flyerCampaign?.userId));
    setExcludedBos(props?.businessObjects?.filter(bo => props?.flyerCampaign?.excludedBoIds?.includes(bo.id)));
  }, [props]);

  return (
    <>
      <h3>Flyer campaign name: {flyerCampaign?.name}</h3>
      <div>
        Location: <Chip size="small" label={flyerCampaign?.address} />
      </div>
      <div>
        Radius: <Chip size="small" label={`${flyerCampaign?.radius}km`} />
      </div>
      <div>
        User: <Chip size="small" label={`[${user?.id}] [${user?.email}] ${user?.fullName}`} />
      </div>
      <div>
        Excluded BO:{' '}
        {excludedBos?.length
          ? excludedBos?.map(bo => (
              <Chip size="small" key={bo?.id} label={`[${bo?.id}] [${bo?.name}] ${bo?.address}`} />
            ))
          : '-'}
      </div>

      <Divider style={{ marginTop: 20, marginBottom: 20 }} />

      <div>
        Available customer: {flyerCampaign?.availableReceiver || '-'} / {flyerCampaign?.totalReceiver || '-'}
      </div>
      <div>
        Affected BO:{' '}
        {flyerCampaign?.businessObjects?.map(bo => (
          <Chip size="small" key={bo?.id} label={`[${bo?.id}] [${bo?.name}] ${bo?.address}`} />
        )) || '-'}
      </div>
      <div>
        <strong>Nr customer to send: {flyerCampaign?.numberToSend || '0'}</strong>
      </div>
      <div>Sms message: {flyerCampaign?.smsMessage || '-'}</div>

      <Divider style={{ marginTop: 20, marginBottom: 20 }} />

      <div>Created at: {flyerCampaign?.createdAt || '-'}</div>
      <div>Last modified: {flyerCampaign?.lastModified || '-'}</div>
      <div>Last sent: {flyerCampaign?.lastSent || '-'}</div>
    </>
  );
}

function SetupLocationStepComponent(props: StepProps) {
  const [businessObjects, setBusinessObjects] = React.useState<BusinessObject[]>([]);

  const [flyerCampaign, setFlyerCampaign] = React.useState<FlyerCampaign>(null);

  const [selectedBusinessObjects, setSelectedBusinessObjects] = React.useState<BusinessObject[]>([]);

  React.useEffect(() => {
    const _initData = async (fc: FlyerCampaign, bos: BusinessObject[]) => {
      setFlyerCampaign(fc || {});
      setBusinessObjects(bos || []);

      if (fc?.excludedBoIds?.length > 0) {
        const selectedBos = bos?.filter(bo => fc?.excludedBoIds?.includes(bo.id));
        setSelectedBusinessObjects(selectedBos || []);
      }
    };

    _initData(props?.flyerCampaign, props?.businessObjects);
  }, [props]);

  React.useEffect(() => {
    setFlyerCampaign({
      ...flyerCampaign,
      excludedBoIds: selectedBusinessObjects?.map(bo => bo.id) || [],
    });
  }, [selectedBusinessObjects]);

  React.useEffect(() => {
    props.onDataChange(flyerCampaign);
  }, [flyerCampaign]);

  const submitDataChangeHandler = (event: any) => {
    const { name, value, type, checked } = event.target;

    setFlyerCampaign({
      ...flyerCampaign,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const classes = useStyles();

  return (
    <>
      <TextField
        variant="outlined"
        margin="dense"
        type="text"
        required
        fullWidth
        key="address"
        label="Address"
        name="address"
        value={flyerCampaign?.address || ''}
        onChange={submitDataChangeHandler}
        helperText={<span>Target address to send flyer campaign</span>}
      />
      <TextField
        variant="outlined"
        margin="dense"
        type="number"
        required
        fullWidth
        key="radius"
        label="Radius"
        name="radius"
        value={flyerCampaign?.radius || ''}
        onChange={submitDataChangeHandler}
        InputProps={{
          endAdornment: <InputAdornment position="end">km</InputAdornment>,
        }}
        helperText={<span>The distance in km from the target address</span>}
      />

      <Divider style={{ marginTop: 20, marginBottom: 20 }} />

      <Autocomplete
        multiple
        autoHighlight
        options={businessObjects}
        filterOptions={(options, { inputValue }) =>
          options.filter(
            item =>
              StringUtils.simplifyString(item.name).includes(StringUtils.simplifyString(inputValue)) ||
              StringUtils.simplifyString(item.address).includes(StringUtils.simplifyString(inputValue)) ||
              StringUtils.simplifyString(item.telephone).includes(StringUtils.simplifyString(inputValue)) ||
              StringUtils.simplifyString(item.owner).includes(StringUtils.simplifyString(inputValue))
          )
        }
        filterSelectedOptions
        value={selectedBusinessObjects}
        onChange={(event: any, newValue) => setSelectedBusinessObjects(newValue as BusinessObject[])}
        getOptionLabel={option => `[${option.id}] ${option.name} [${option.address}]`}
        renderOption={(props, option: any) => {
          const { key, ...optionProps } = props;

          return (
            <Box key={key} component="li" {...optionProps}>
              <div>
                [{option.id}]&nbsp;<strong>{option.name}</strong>
                <div className={classes.optionDescription}>
                  {option.address}
                  <div>{option.owner}</div>
                </div>
              </div>
            </Box>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            label="Select salons to exclude from flyer campaign"
            variant="outlined"
            inputProps={{
              ...params.inputProps,
              autoComplete: 'salon-to-exclude', // disable autocomplete and autofill
            }}
          />
        )}
      />
    </>
  );
}

function EditInfoStepComponent(props: StepProps) {
  const [users, setUsers] = React.useState<User[]>([]);
  const [selectedUser, setSelectedUser] = React.useState<User>(null);

  const [flyerCampaign, setFlyerCampaign] = React.useState<FlyerCampaign>({});

  React.useEffect(() => {
    const _initData = async (fc: FlyerCampaign, us: User[]) => {
      setFlyerCampaign({ ...(fc || {}) });
      setUsers(us || []);

      if (fc?.userId) {
        const user = users?.find(u => u.id === fc?.userId);
        setSelectedUser(user || null);
      }
    };

    _initData(props?.flyerCampaign, props?.users);
  }, [props]);

  React.useEffect(() => {
    setFlyerCampaign({
      ...flyerCampaign,
      userId: selectedUser?.id,
      userEmail: selectedUser?.email,
    });
  }, [selectedUser]);

  React.useEffect(() => {
    props.onDataChange(flyerCampaign);
  }, [flyerCampaign]);

  const submitDataChangeHandler = (event: any) => {
    const { name, value, type, checked } = event.target;

    setFlyerCampaign({
      ...flyerCampaign,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const classes = useStyles();

  return (
    <>
      <h2>
        Available customer: {flyerCampaign?.availableReceiver} / {flyerCampaign?.totalReceiver}
      </h2>

      <div>
        Customer of following salons are affected:{' '}
        {flyerCampaign?.businessObjects?.map(bo => (
          <Chip size="small" key={bo?.id} label={`[${bo?.id}] [${bo?.name}] ${bo?.address}`} />
        ))}
      </div>

      <Divider style={{ marginTop: 20, marginBottom: 20 }} />

      <TextField
        variant="outlined"
        margin="dense"
        type="text"
        required
        fullWidth
        key="name"
        label="Name of campaign"
        name="name"
        value={flyerCampaign?.name || ''}
        onChange={submitDataChangeHandler}
      />
      <TextField
        variant="outlined"
        margin="dense"
        type="text"
        required
        multiline
        fullWidth
        key="smsMessage"
        label="SMS message"
        name="smsMessage"
        value={flyerCampaign?.smsMessage || ''}
        onChange={submitDataChangeHandler}
      />
      <Autocomplete
        autoHighlight
        options={users?.sort((a, b) => -b.locale?.localeCompare(a.locale))}
        groupBy={option => option.locale}
        filterOptions={(options, { inputValue }) =>
          options.filter(
            item =>
              StringUtils.simplifyString(item.email).includes(StringUtils.simplifyString(inputValue)) ||
              StringUtils.simplifyString(item.phoneNr).includes(StringUtils.simplifyString(inputValue)) ||
              StringUtils.simplifyString(item.fullName).includes(StringUtils.simplifyString(inputValue))
          )
        }
        value={selectedUser}
        onChange={(event: any, newValue) => setSelectedUser(newValue as User)}
        getOptionLabel={option => `[${option.id}] [${option.email}] [${option.fullName}]`}
        renderOption={(props, option: any) => {
          const { key, ...optionProps } = props;

          return (
            <Box key={key} component="li" {...optionProps}>
              <div>
                [{option.id}]&nbsp;<strong>{option.email}</strong>
                <div className={classes.optionDescription}>
                  {option.fullName}
                  <div>{option.phoneNr}</div>
                </div>
              </div>
            </Box>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            required
            label="User"
            variant="outlined"
            inputProps={{
              ...params.inputProps,
              autoComplete: 'user-to-assign', // disable autocomplete and autofill
            }}
          />
        )}
      />
    </>
  );
}

function EstimatedPriceStepComponent(props: StepProps) {
  const [isLoading, setIsLoading] = React.useState<boolean>(false);

  const [flyerCampaign, setFlyerCampaign] = React.useState<FlyerCampaign>(null);

  const [invoices, setInvoices] = React.useState<InvoiceDetail[]>([]);
  const [newInvoiceItemName, setNewInvoiceItemName] = React.useState<string>('');
  const [newInvoiceAmount, setNewInvoiceAmount] = React.useState<number>(null);

  const _fetchInvoiceOfFlyerCampaign = async (fc: FlyerCampaign) => {
    if (fc?.invoiceIds?.length > 0) {
      setIsLoading(true);
      const createdInvoices = await adminService.fetchAllInvoicesById(fc?.invoiceIds);
      setInvoices(createdInvoices || []);
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    const _initData = async (fc: FlyerCampaign) => {
      const nextAvailableCustomerToSend = Math.min(
        DEFAULT_NUMBER_OF_CUSTOMER_FOR_FLYER_CAMPAIGN,
        fc?.availableReceiver
      );

      setFlyerCampaign({
        ...fc,
        numberToSend: Math.min(fc?.numberToSend || 0, nextAvailableCustomerToSend),
      });
    };

    _initData(props?.flyerCampaign);
  }, [props]);

  React.useEffect(() => {
    props.onDataChange(flyerCampaign);

    if (!invoices?.length) {
      _fetchInvoiceOfFlyerCampaign(flyerCampaign);
    }
  }, [flyerCampaign]);

  const submitDataChangeHandler = (event: any) => {
    const { name, value, type, checked } = event.target;

    setFlyerCampaign({
      ...flyerCampaign,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const isAbleToCreateInvoiceForFlyerCampaign = (): boolean => {
    return !isLoading && !!flyerCampaign?.id && !!newInvoiceItemName && !!newInvoiceAmount && newInvoiceAmount > 0;
  };

  const createInvoiceForFlyerCampaign = async () => {
    if (!isAbleToCreateInvoiceForFlyerCampaign()) {
      return;
    }

    try {
      setIsLoading(true);

      const selectedNumberToSend = flyerCampaign?.numberToSend || 0;

      await flyerCampaignService.createInvoiceForFlyerCampaign(flyerCampaign?.id, newInvoiceItemName, newInvoiceAmount);

      const updatedFlyerCampaign = await flyerCampaignService.refreshFlyerCampaign(flyerCampaign?.id);
      setFlyerCampaign({
        ...updatedFlyerCampaign,
        numberToSend: selectedNumberToSend,
      });
      await _fetchInvoiceOfFlyerCampaign(updatedFlyerCampaign);
    } catch (e) {
      logger.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <h2>
        Available customer: {flyerCampaign?.availableReceiver} / {flyerCampaign?.totalReceiver}
      </h2>
      <Divider style={{ marginTop: 20, marginBottom: 20 }} />
      <TextField
        variant="outlined"
        margin="dense"
        type="number"
        required
        fullWidth
        key="numberToSend"
        label="Number of customer to send"
        name="numberToSend"
        value={flyerCampaign?.numberToSend || ''}
        onChange={submitDataChangeHandler}
      />
      <div>
        <strong>
          Estimated cost: {flyerCampaign?.numberToSend} * {FLYER_CAMPAIGN_PRICE_PER_SMS} ={' '}
          {flyerCampaign?.numberToSend * FLYER_CAMPAIGN_PRICE_PER_SMS} EUR
        </strong>
      </div>

      <Divider style={{ marginTop: 20, marginBottom: 20 }} />

      <InvoiceTableComponent invoices={invoices} onSelect={(invoice: InvoiceDetail) => {}} />
      {isLoading ? <LinearProgress /> : <></>}

      <Grid container spacing={3}>
        <Grid item xs={6}>
          <TextField
            variant="outlined"
            margin="dense"
            type="text"
            required
            fullWidth
            key="newInvoiceItemName"
            label="New invoice item name"
            name="newInvoiceItemName"
            value={newInvoiceItemName || ''}
            onChange={(event: any) => setNewInvoiceItemName(event.target.value)}
            helperText={
              <span>
                E.g. Flyer campaign "{flyerCampaign?.name}": cost for {flyerCampaign?.numberToSend} messages
              </span>
            }
          />
        </Grid>

        <Grid item xs={3}>
          <TextField
            variant="outlined"
            margin="dense"
            type="number"
            required
            fullWidth
            label="Amount"
            name="amount"
            value={newInvoiceAmount || ''}
            InputProps={{
              endAdornment: <InputAdornment position="end">EUR</InputAdornment>,
            }}
            onChange={(event: any) => setNewInvoiceAmount(event.target.value)}
          />
        </Grid>

        <Grid item xs={3}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            disabled={!isAbleToCreateInvoiceForFlyerCampaign()}
            onClick={createInvoiceForFlyerCampaign}
          >
            Create invoice
          </Button>
        </Grid>
      </Grid>
    </>
  );
}

function ReviewAndSubmitStepComponent(props: StepProps) {
  const [businessObjects, setBusinessObjects] = React.useState<BusinessObject[]>([]);

  const [flyerCampaign, setFlyerCampaign] = React.useState<FlyerCampaign>(null);

  const [selectedBusinessObject, setSelectedBusinessObject] = React.useState<BusinessObject>(null);

  React.useEffect(() => {
    setFlyerCampaign(props?.flyerCampaign || {});

    const bosOfUser = props?.businessObjects?.filter(bo => bo.owner === props?.flyerCampaign?.userEmail) || [];

    setBusinessObjects(bosOfUser);
    setSelectedBusinessObject(bosOfUser?.[0] || null);
  }, [props]);

  React.useEffect(() => {
    setFlyerCampaign({
      ...flyerCampaign,
      selectedBoIdToSend: selectedBusinessObject?.id,
    });
  }, [selectedBusinessObject]);

  React.useEffect(() => {
    props.onDataChange(flyerCampaign);
  }, [flyerCampaign]);

  const classes = useStyles();

  return (
    <>
      <h2>Overview</h2>

      <InfoStep
        flyerCampaign={props?.flyerCampaign}
        users={props?.users}
        businessObjects={props?.businessObjects}
        onDataChange={() => {}}
      />

      <Divider style={{ marginTop: 20, marginBottom: 20 }} />

      <Autocomplete
        autoHighlight
        options={businessObjects}
        filterOptions={(options, { inputValue }) =>
          options.filter(
            item =>
              StringUtils.simplifyString(item.name).includes(StringUtils.simplifyString(inputValue)) ||
              StringUtils.simplifyString(item.address).includes(StringUtils.simplifyString(inputValue)) ||
              StringUtils.simplifyString(item.telephone).includes(StringUtils.simplifyString(inputValue)) ||
              StringUtils.simplifyString(item.owner).includes(StringUtils.simplifyString(inputValue))
          )
        }
        filterSelectedOptions
        value={selectedBusinessObject}
        onChange={(event: any, newValue) => setSelectedBusinessObject(newValue as BusinessObject)}
        getOptionLabel={option => `[${option.id}] ${option.name} [${option.address}]`}
        renderOption={(props, option: any) => {
          const { key, ...optionProps } = props;

          return (
            <Box key={key} component="li" {...optionProps}>
              <div>
                [{option.id}]&nbsp;<strong>{option.name}</strong>
                <div className={classes.optionDescription}>
                  {option.address}
                  <div>{option.owner}</div>
                </div>
              </div>
            </Box>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            required={true}
            label="Select salon to send flyer campaign"
            variant="outlined"
            inputProps={{
              ...params.inputProps,
              autoComplete: 'salon-to-send', // disable autocomplete and autofill
            }}
          />
        )}
      />

      <Divider style={{ marginTop: 20, marginBottom: 20 }} />

      <div>
        <strong>Important: Only send flyer campaign after invoice is paid!</strong>
      </div>
    </>
  );
}

enum STEP {
  SETUP_LOCATION = 'Setup location',
  EDIT_INFO = 'Edit info',
  INFO = 'Info',
  ESTIMATED_PRICE = 'Estimated price',
  REVIEW_AND_SUBMIT = 'Review and submit',
}

interface FlyerCampaignDialogComponentProps {
  isOpen: boolean;
  data: FlyerCampaign;
  onClose: Function;
}

export default function FlyerCampaignDialogComponent(props: FlyerCampaignDialogComponentProps) {
  const [steps, setSteps] = React.useState<STEP[]>([]);
  const [activeStepIndex, setActiveStepIndex] = React.useState<number>(0);

  const [businessObjects, setBusinessObjects] = React.useState<BusinessObject[]>([]);
  const [users, setUsers] = React.useState<User[]>([]);

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [flyerCampaign, setFlyerCampaign] = React.useState<FlyerCampaign>({});

  const _isAbleToPrepareFlyerCampaign = (): boolean => {
    return !isLoading && !!flyerCampaign?.address && !!flyerCampaign?.radius;
  };

  const _prepareFlyerCampaign = async () => {
    if (!_isAbleToPrepareFlyerCampaign()) {
      return;
    }

    setIsLoading(true);
    try {
      const data = await flyerCampaignService.prepareNewFlyerCampaign(
        flyerCampaign?.address,
        flyerCampaign?.radius,
        flyerCampaign?.excludedBoIds
      );

      _nextStep();

      // workaround to fix the bug: the next step does not render at the beginning
      setTimeout(() => {
        setFlyerCampaign({
          ...flyerCampaign,
          ...data,
        });
      }, 100);
    } catch (e) {
      logger.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const _isAbleToRefreshFlyerCampaign = (): boolean => {
    return !isLoading && !!flyerCampaign?.id;
  };

  const _refreshFlyerCampaign = async () => {
    if (!_isAbleToRefreshFlyerCampaign()) {
      return;
    }

    setIsLoading(true);
    try {
      const data = await flyerCampaignService.refreshFlyerCampaign(flyerCampaign?.id);

      _nextStep();

      // workaround to fix the bug: the next step does not render at the beginning
      setTimeout(() => {
        setFlyerCampaign({
          ...flyerCampaign,
          ...data,
        });
      }, 100);
    } catch (e) {
      logger.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const _isAbleToSaveFlyerCampaign = (): boolean => {
    return (
      !isLoading &&
      !!flyerCampaign?.name &&
      !!flyerCampaign?.smsMessage &&
      !!flyerCampaign?.userId &&
      !!flyerCampaign?.userEmail
    );
  };

  const _saveFlyerCampaign = async () => {
    if (!_isAbleToSaveFlyerCampaign()) {
      return;
    }

    setIsLoading(true);
    try {
      const data = await flyerCampaignService.saveFlyerCampaign(flyerCampaign);

      _nextStep();

      // workaround to fix the bug: the next step does not render at the beginning
      setTimeout(() => {
        setFlyerCampaign({
          ...flyerCampaign,
          ...data,
        });
      }, 100);
    } catch (e) {
      logger.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const _isAbleToSendFlyerCampaign = (): boolean => {
    return (
      !isLoading &&
      !!flyerCampaign?.id &&
      !!flyerCampaign?.selectedBoIdToSend &&
      !!flyerCampaign?.numberToSend &&
      flyerCampaign?.numberToSend > 0
    );
  };

  const _sendFlyerCampaign = async () => {
    if (!_isAbleToSendFlyerCampaign()) {
      return;
    }

    setIsLoading(true);
    try {
      const data = await flyerCampaignService.sendFlyerCampaign(
        flyerCampaign?.id,
        flyerCampaign?.numberToSend,
        flyerCampaign?.selectedBoIdToSend
      );

      // workaround to fix the bug: the next step does not render at the beginning
      setTimeout(() => {
        setFlyerCampaign({
          ...flyerCampaign,
          ...data,
        });
      }, 100);

      _closeDialog();
    } catch (e) {
      logger.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const _nextStep = () => {
    setActiveStepIndex(activeStepIndex + 1);
  };

  const _closeDialog = () => {
    props.onClose();
  };

  const _onFlyerCampaignChanged = (fc: FlyerCampaign) => {
    // only update the flyer campaign if there is change to avoid endless rerenders
    const currentStep: STEP = steps[activeStepIndex];

    if (
      currentStep === STEP.SETUP_LOCATION &&
      fc?.address === flyerCampaign?.address &&
      fc?.radius == flyerCampaign?.radius &&
      ArrayUtils.isSameArray(fc?.excludedBoIds, flyerCampaign?.excludedBoIds)
    ) {
      return;
    }

    if (
      currentStep === STEP.EDIT_INFO &&
      fc?.name === flyerCampaign?.name &&
      fc?.smsMessage === flyerCampaign?.smsMessage &&
      fc?.userId == flyerCampaign?.userId &&
      fc?.userEmail === flyerCampaign?.userEmail
    ) {
      return;
    }

    if (currentStep === STEP.ESTIMATED_PRICE && fc?.numberToSend == flyerCampaign?.numberToSend) {
      return;
    }

    if (currentStep === STEP.REVIEW_AND_SUBMIT && fc?.selectedBoIdToSend == flyerCampaign?.selectedBoIdToSend) {
      return;
    }

    setFlyerCampaign({
      ...flyerCampaign,
      ...fc,
    });
  };

  React.useEffect(() => {
    const _init = async () => {
      setIsLoading(true);

      const bos = await businessObjectService.getAllBusinessObjects();
      setBusinessObjects(bos || []);

      const us = await userService.getAllBusinessOwnerUsers();
      setUsers(us || []);

      setIsLoading(false);
    };

    _init();

    setFlyerCampaign(props.data);

    if (!props.data?.id) {
      setSteps([STEP.SETUP_LOCATION, STEP.EDIT_INFO, STEP.ESTIMATED_PRICE, STEP.REVIEW_AND_SUBMIT]);
    } else {
      setSteps([STEP.INFO, STEP.ESTIMATED_PRICE, STEP.REVIEW_AND_SUBMIT]);
    }
  }, [props]);

  const getTitle = () => {
    return flyerCampaign?.id ? `Edit flyer campaign with id=${flyerCampaign.id}` : 'Create new flyer campaign';
  };

  const getContent = () => {
    return (
      <>
        <Stepper activeStep={activeStepIndex}>
          {steps?.map((step, index) => {
            return (
              <Step key={step} completed={index < activeStepIndex}>
                <StepLabel>{step}</StepLabel>
              </Step>
            );
          })}
        </Stepper>

        {steps[activeStepIndex] === STEP.INFO ? (
          <InfoStep
            flyerCampaign={flyerCampaign}
            users={users}
            businessObjects={businessObjects}
            onDataChange={_onFlyerCampaignChanged}
          />
        ) : (
          <></>
        )}

        {steps[activeStepIndex] === STEP.SETUP_LOCATION ? (
          <SetupLocationStepComponent
            flyerCampaign={flyerCampaign}
            businessObjects={businessObjects}
            onDataChange={_onFlyerCampaignChanged}
          />
        ) : (
          <></>
        )}

        {steps[activeStepIndex] === STEP.EDIT_INFO ? (
          <EditInfoStepComponent flyerCampaign={flyerCampaign} users={users} onDataChange={_onFlyerCampaignChanged} />
        ) : (
          <></>
        )}

        {steps[activeStepIndex] === STEP.ESTIMATED_PRICE ? (
          <EstimatedPriceStepComponent flyerCampaign={flyerCampaign} onDataChange={_onFlyerCampaignChanged} />
        ) : (
          <></>
        )}

        {steps[activeStepIndex] === STEP.REVIEW_AND_SUBMIT ? (
          <ReviewAndSubmitStepComponent
            flyerCampaign={flyerCampaign}
            users={users}
            businessObjects={businessObjects}
            onDataChange={_onFlyerCampaignChanged}
          />
        ) : (
          <></>
        )}
      </>
    );
  };

  const isNextButtonDisabled = (): boolean => {
    if (isLoading) {
      return true;
    }

    const currentStep: STEP = steps[activeStepIndex];

    switch (currentStep) {
      case STEP.SETUP_LOCATION:
        return !_isAbleToPrepareFlyerCampaign();
      case STEP.EDIT_INFO:
        return !_isAbleToSaveFlyerCampaign();
      case STEP.INFO:
        return !_isAbleToRefreshFlyerCampaign();
      case STEP.ESTIMATED_PRICE:
        return !flyerCampaign?.numberToSend || flyerCampaign?.numberToSend <= 0;
      case STEP.REVIEW_AND_SUBMIT:
        return !_isAbleToSendFlyerCampaign();
    }
  };

  const next = async () => {
    const currentStep: STEP = steps[activeStepIndex];

    switch (currentStep) {
      case STEP.SETUP_LOCATION:
        return _prepareFlyerCampaign();
      case STEP.EDIT_INFO:
        return _saveFlyerCampaign();
      case STEP.INFO:
        return _refreshFlyerCampaign();
      case STEP.ESTIMATED_PRICE:
        return _nextStep();
      case STEP.REVIEW_AND_SUBMIT:
        return _sendFlyerCampaign();
    }
  };

  const getButtons = () => {
    return (
      <Button fullWidth variant="contained" color="primary" disabled={isNextButtonDisabled()} onClick={next}>
        {activeStepIndex === steps.length - 1 ? 'Send' : 'Next'}
      </Button>
    );
  };

  return (
    <CustomDialogComponent
      isLoading={isLoading}
      styleOptions={{ fullWidth: true }}
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={getTitle()}
      content={getContent()}
      buttons={getButtons()}
    ></CustomDialogComponent>
  );
}
